/**
 * Validation Engine — applies validation-rule definitions to a form instance.
 *
 * Responsibilities (pure functions, no React, no Supabase):
 *   - Single-rule evaluation: `checkValidationRule(rule, val, allValues)`
 *     returns true when the rule FAILS (is triggered).
 *   - Aggregate evaluation: `detectBlockingErrors` and `detectSoftWarnings`
 *     accept the RenderState map produced by the condition engine, so any
 *     dynamic `required` flip / option filter / visibility change from a
 *     conditional rule is automatically reflected in validation output.
 *   - Pre-upload file checks: `checkFileAgainstRules` runs file/image rules
 *     against a picked `File` object before the storage write.
 *
 * Rule *definitions* live in `rule-catalog.ts`; this module only APPLIES them.
 */
import type {
  FieldDefinition,
  ValidationRule,
} from './definition-types'
import type { RenderState, BlockingError, SoftWarning, UploadCheckResult } from './types'

function isUploadedFileRef(
  v: unknown,
): v is { originalName: string; mimeType: string; sizeBytes: number } {
  return (
    !!v &&
    typeof v === 'object' &&
    'originalName' in (v as Record<string, unknown>) &&
    'sizeBytes' in (v as Record<string, unknown>)
  )
}

function resolveRelativeDate(cfg: Record<string, unknown>): Date | null {
  const offsetValue = cfg.offset_value as number | undefined
  const offsetUnit = (cfg.offset_unit as string | undefined) ?? 'years'
  if (offsetValue === undefined) return null
  const d = new Date()
  if (offsetUnit === 'days') d.setDate(d.getDate() + offsetValue)
  else if (offsetUnit === 'months') d.setMonth(d.getMonth() + offsetValue)
  else d.setFullYear(d.getFullYear() + offsetValue)
  return d
}

/**
 * Returns true when the rule is *triggered* (validation FAILS).
 * Pure: same `(rule, val, allValues)` always returns the same result.
 */
export function checkValidationRule(
  rule: ValidationRule,
  val: unknown,
  allValues: Record<string, unknown>,
): boolean {
  const cfg = rule.rule_config ?? {}
  switch (rule.rule_type) {
    case 'min_length':
      return typeof val === 'string' && val.length < Number(cfg.min ?? 0)
    case 'max_length':
      return typeof val === 'string' && val.length > Number(cfg.max ?? Infinity)
    case 'regex_match': {
      if (typeof val !== 'string' || !cfg.pattern) return false
      try { return !new RegExp(cfg.pattern as string).test(val) } catch { return false }
    }

    case 'numeric_range':
    case 'physiological_range': {
      const n = Number(val)
      if (val === '' || val === null || val === undefined || isNaN(n)) return false
      const min = cfg.min as number | undefined
      const max = cfg.max as number | undefined
      return (min !== undefined && n < min) || (max !== undefined && n > max)
    }
    case 'max_decimal_places': {
      if (val === '' || val === null || val === undefined) return false
      const str = String(val)
      const dot = str.indexOf('.')
      if (dot === -1) return false
      return str.length - dot - 1 > Number(cfg.max_decimals ?? 0)
    }
    case 'integer_only': {
      if (val === '' || val === null || val === undefined) return false
      const n = Number(val)
      if (isNaN(n)) return false
      return !Number.isInteger(n)
    }
    case 'value_greater_than_field': {
      const other = allValues[cfg.compare_field_key as string]
      if (val === '' || val == null || other === '' || other == null) return false
      return Number(val) <= Number(other)
    }
    case 'value_less_than_field': {
      const other = allValues[cfg.compare_field_key as string]
      if (val === '' || val == null || other === '' || other == null) return false
      return Number(val) >= Number(other)
    }

    case 'date_in_past':
      return typeof val === 'string' && val !== '' && new Date(val) >= new Date()
    case 'date_in_future':
      return typeof val === 'string' && val !== '' && new Date(val) <= new Date()
    case 'date_after_absolute': {
      if (typeof val !== 'string' || !val || !cfg.date) return false
      return new Date(val) <= new Date(cfg.date as string)
    }
    case 'date_before_absolute': {
      if (typeof val !== 'string' || !val || !cfg.date) return false
      return new Date(val) >= new Date(cfg.date as string)
    }
    case 'date_after_relative': {
      if (typeof val !== 'string' || !val) return false
      const ref = resolveRelativeDate(cfg)
      return ref ? new Date(val) <= ref : false
    }
    case 'date_before_relative': {
      if (typeof val !== 'string' || !val) return false
      const ref = resolveRelativeDate(cfg)
      return ref ? new Date(val) >= ref : false
    }
    case 'date_after_field': {
      const other = allValues[cfg.compare_field_key as string]
      if (typeof val !== 'string' || typeof other !== 'string' || !val || !other) return false
      return new Date(val) <= new Date(other)
    }
    case 'date_before_field': {
      const other = allValues[cfg.compare_field_key as string]
      if (typeof val !== 'string' || typeof other !== 'string' || !val || !other) return false
      return new Date(val) >= new Date(other)
    }

    case 'time_after': {
      if (typeof val !== 'string' || !val || !cfg.time) return false
      return val <= (cfg.time as string)
    }
    case 'time_before': {
      if (typeof val !== 'string' || !val || !cfg.time) return false
      return val >= (cfg.time as string)
    }

    case 'min_selected':
      return Array.isArray(val) && val.length < Number(cfg.min ?? 0)
    case 'max_selected':
      return Array.isArray(val) && val.length > Number(cfg.max ?? Infinity)

    case 'min_entries':
      return Array.isArray(val) && val.length < Number(cfg.min ?? 0)
    case 'max_entries':
      return Array.isArray(val) && val.length > Number(cfg.max ?? Infinity)

    case 'file_extension': {
      if (!isUploadedFileRef(val)) return false
      const allowed = (cfg.extensions as string[] | undefined) ?? []
      if (allowed.length === 0) return false
      const ext = (val.originalName.split('.').pop() ?? '').toLowerCase()
      return !allowed.map((e) => e.replace(/^\./, '').toLowerCase()).includes(ext)
    }
    case 'max_file_size': {
      if (!isUploadedFileRef(val)) return false
      const maxMb = Number(cfg.max_mb ?? 0)
      if (!maxMb) return false
      return val.sizeBytes > maxMb * 1024 * 1024
    }
    case 'max_image_width':
    case 'max_image_height':
      return false

    default:
      return false
  }
}

function isEmptyValue(val: unknown): boolean {
  return (
    val === null ||
    val === undefined ||
    val === '' ||
    (Array.isArray(val) && val.length === 0)
  )
}

/**
 * Run required-checks + blocking rules. Visibility / required come from the
 * RenderState map (which is the condition engine's output), so a conditional
 * `hide` / `unrequire` will short-circuit validation for that field.
 */
export function detectBlockingErrors(
  fields: FieldDefinition[],
  renderStates: Record<string, RenderState>,
  values: Record<string, unknown>,
): Record<string, BlockingError> {
  const errors: Record<string, BlockingError> = {}
  for (const field of fields) {
    const state = renderStates[field.field_key]
    if (!state?.visible) continue
    const val = values[field.field_key]
    const fieldLabel = field.label.fa || field.field_key

    if (state.required && isEmptyValue(val)) {
      errors[field.field_key] = {
        fieldKey: field.field_key,
        fieldLabel,
        ruleId: null,
        message: 'این فیلد اجباری است',
      }
      continue
    }
    if (isEmptyValue(val)) continue

    for (const rule of field.validations ?? []) {
      if (!rule.is_enabled || rule.severity !== 'blocking') continue
      if (checkValidationRule(rule, val, values)) {
        errors[field.field_key] = {
          fieldKey: field.field_key,
          fieldLabel,
          ruleId: rule.rule_id,
          message: rule.error_message?.fa ?? defaultErrorFor(rule),
        }
        break
      }
    }
  }
  return errors
}

export function formatValueForDisplay(field: FieldDefinition, val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (Array.isArray(val)) {
    if (field.field_type === 'multi_select') {
      const opts =
        (field.type_config?.options as Array<{
          option_key: string
          label: { fa: string }
        }> | undefined) ?? []
      return (val as string[])
        .map((k) => opts.find((o) => o.option_key === k)?.label.fa ?? k)
        .join('، ')
    }
    return `${val.length} مورد`
  }
  if (field.field_type === 'single_select') {
    const opts =
      (field.type_config?.options as Array<{
        option_key: string
        label: { fa: string }
      }> | undefined) ?? []
    return opts.find((o) => o.option_key === val)?.label.fa ?? String(val)
  }
  if (field.field_type === 'boolean') return val ? 'بله' : 'خیر'
  if (isUploadedFileRef(val)) return val.originalName
  return String(val)
}

export function detectSoftWarnings(
  fields: FieldDefinition[],
  renderStates: Record<string, RenderState>,
  values: Record<string, unknown>,
): SoftWarning[] {
  const warnings: SoftWarning[] = []
  for (const field of fields) {
    const state = renderStates[field.field_key]
    if (!state?.visible) continue
    const val = values[field.field_key]
    if (isEmptyValue(val)) continue
    for (const rule of field.validations ?? []) {
      if (!rule.is_enabled || rule.severity !== 'soft_warning') continue
      if (checkValidationRule(rule, val, values)) {
        warnings.push({
          fieldKey: field.field_key,
          fieldLabel: field.label.fa || field.field_key,
          ruleId: rule.rule_id,
          message: rule.error_message?.fa ?? defaultErrorFor(rule),
          currentValue: formatValueForDisplay(field, val),
        })
      }
    }
  }
  return warnings
}

export function defaultErrorFor(rule: ValidationRule): string {
  const cfg = rule.rule_config ?? {}
  switch (rule.rule_type) {
    case 'min_length':              return `حداقل ${cfg.min} کاراکتر`
    case 'max_length':              return `حداکثر ${cfg.max} کاراکتر`
    case 'regex_match':             return 'فرمت نامعتبر'
    case 'numeric_range':           return `مقدار باید بین ${cfg.min ?? '∞-'} و ${cfg.max ?? '∞+'} باشد`
    case 'physiological_range':     return 'مقدار خارج از محدوده فیزیولوژیک'
    case 'max_decimal_places':      return `حداکثر ${cfg.max_decimals} رقم اعشار مجاز است`
    case 'integer_only':            return 'فقط عدد صحیح مجاز است'
    case 'min_selected':            return `حداقل ${cfg.min} گزینه انتخاب کنید`
    case 'max_selected':            return `حداکثر ${cfg.max} گزینه انتخاب کنید`
    case 'min_entries':             return `حداقل ${cfg.min} ردیف لازم است`
    case 'max_entries':             return `حداکثر ${cfg.max} ردیف مجاز است`
    case 'date_in_past':            return 'تاریخ باید در گذشته باشد'
    case 'date_in_future':          return 'تاریخ باید در آینده باشد'
    case 'date_after_absolute':     return `تاریخ باید بعد از ${cfg.date} باشد`
    case 'date_before_absolute':    return `تاریخ باید قبل از ${cfg.date} باشد`
    case 'time_after':              return `زمان باید بعد از ${cfg.time} باشد`
    case 'time_before':             return `زمان باید قبل از ${cfg.time} باشد`
    case 'file_extension':          return `پسوند فایل مجاز نیست`
    case 'max_file_size':           return `حداکثر حجم ${cfg.max_mb} مگابایت`
    case 'max_image_width':         return `حداکثر عرض ${cfg.max_px} پیکسل`
    case 'max_image_height':        return `حداکثر ارتفاع ${cfg.max_px} پیکسل`
    default:                        return 'خطای اعتبارسنجی'
  }
}

// ─── Upload-time helpers (used by FileFieldInput, before storage write) ──────

/**
 * Platform-neutral shape of a picked-but-not-yet-uploaded file. The web passes a
 * DOM `File` (which structurally satisfies `name`/`size`/`type`); React Native
 * passes the normalized asset from expo-image/document-picker (`mimeType`, and
 * `width`/`height` already resolved). This keeps the package 100% DOM-free.
 */
export interface PickedFileLike {
  name: string
  size: number
  /** Web `File.type`. */
  type?: string
  /** RN picker mime type (fallback when `type` is absent). */
  mimeType?: string
  /** Pixel dimensions if the platform already knows them (RN picker provides these). */
  width?: number
  height?: number
}

/**
 * Resolves the pixel dimensions of an image file. The browser implementation
 * (`URL.createObjectURL` + `new Image()`) lives in `apps/web`; RN never needs
 * it because the picker returns dimensions on the asset. Returns `null` when
 * dimensions cannot be determined (rule is then skipped, not failed).
 */
export type ImageDimensionProbe = (
  file: PickedFileLike,
) => Promise<{ width: number; height: number } | null>

function mimeOf(file: PickedFileLike): string {
  return file.type ?? file.mimeType ?? ''
}

export async function checkFileAgainstRules(
  file: PickedFileLike,
  rules: ValidationRule[],
  probe?: ImageDimensionProbe,
): Promise<UploadCheckResult> {
  for (const rule of rules) {
    if (!rule.is_enabled || rule.severity !== 'blocking') continue
    const cfg = rule.rule_config ?? {}
    if (rule.rule_type === 'file_extension') {
      const allowed = ((cfg.extensions as string[] | undefined) ?? [])
        .map((e) => e.replace(/^\./, '').toLowerCase())
      if (allowed.length === 0) continue
      const ext = (file.name.split('.').pop() ?? '').toLowerCase()
      if (!allowed.includes(ext)) {
        return { ok: false, message: rule.error_message?.fa ?? defaultErrorFor(rule) }
      }
    }
    if (rule.rule_type === 'max_file_size') {
      const maxMb = Number(cfg.max_mb ?? 0)
      if (maxMb && file.size > maxMb * 1024 * 1024) {
        return { ok: false, message: rule.error_message?.fa ?? defaultErrorFor(rule) }
      }
    }
    if (rule.rule_type === 'max_image_width' || rule.rule_type === 'max_image_height') {
      if (!mimeOf(file).startsWith('image/')) continue
      // Prefer dimensions the platform already resolved (RN picker); otherwise
      // fall back to the injected probe (web DOM). No probe + no dims → skip.
      const dims =
        file.width != null && file.height != null
          ? { width: file.width, height: file.height }
          : probe
            ? await probe(file)
            : null
      if (!dims) continue
      const maxPx = Number(cfg.max_px ?? 0)
      if (!maxPx) continue
      if (rule.rule_type === 'max_image_width' && dims.width > maxPx) {
        return { ok: false, message: rule.error_message?.fa ?? defaultErrorFor(rule) }
      }
      if (rule.rule_type === 'max_image_height' && dims.height > maxPx) {
        return { ok: false, message: rule.error_message?.fa ?? defaultErrorFor(rule) }
      }
    }
  }
  return { ok: true }
}

export function acceptAttrFromRules(rules: ValidationRule[]): string | undefined {
  const exts: string[] = []
  for (const r of rules) {
    if (!r.is_enabled || r.rule_type !== 'file_extension') continue
    const list = (r.rule_config?.extensions as string[] | undefined) ?? []
    for (const e of list) exts.push('.' + e.replace(/^\./, ''))
  }
  return exts.length ? Array.from(new Set(exts)).join(',') : undefined
}
